import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { select, Store } from "@ngrx/store";
import { Observable, Subscription } from "rxjs";
import { filterNullOrUndefined } from 'src/app/@operators/filter-null-or-undefined';
import { ContractDto, InsurerDto, QuestionAnswersDto, QuestionnaireDto, QuestionType, RefLanguageDto, SiteDto } from "src/app/api/models";
import { AppState, getState } from "src/app/state/app.state";
import * as ArcHospiActions from "../../archospi/state/archospi.actions";
import { setTitle } from "../../title/state/title.actions";
import { setOverwritePreHospiConfirmed, setCurrentQuestion, addAnswersSummary, removeAnswersSummary, setCurrentAnswers, setQuestionCompleted, setConfirmedSite } from "../../transaction-info/state/transaction-info.actions";
import { getAnswerLightInfo, getCurrentAnswers, getCurrentQuestion, getQuestionnaire } from "../../transaction-info/state/transaction-info.selectors";
import * as QuestionsActions from "../state/question.actions";
import { validateAnswer } from "../state/question.actions";
import { environment } from "src/environments/environment";

@Component({
    selector: "app-question",
    templateUrl: "./question.component.html",
    styleUrls: ["./question.component.scss"],
    standalone: false
})
export class QuestionComponent implements OnInit,OnDestroy {
	@ViewChild("scrollToTop") scrollToTop: ElementRef;
	public currentAnswers$: Observable<QuestionAnswersDto[]> = this.store.pipe(select(getCurrentAnswers)).pipe(filterNullOrUndefined());
	public currentQuestion: QuestionAnswersDto;
	public questionnaire: QuestionnaireDto = undefined;
	public properyToDisplay = "text";
	public answer: string;

	private language: RefLanguageDto = undefined;
	private insurer: InsurerDto;
	private admissionDate: Date;
	private contract: ContractDto;
	private site: SiteDto;
	private subs: Subscription[] = [];
	private state: AppState;
	public kioskMode = false;
	constructor(private store: Store<AppState>) {
		this.subs.push(
			getState(this.store).subscribe(state =>{
				this.state = state;
				this.language = state.transactionInfo.selectedLanguage;
				this.admissionDate = state.transactionInfo.admissionDate;
				this.insurer = state.transactionInfo.selectedInsurer;
				this.contract = state.transactionInfo.selectedContract;
				this.site = state.transactionInfo.selectedSite;
			})
		);
	}

	ngOnInit(): void {
		this.kioskMode = environment.kioskMode;

		if(this.state.transactionInfo.questionnaire == undefined)
			this.store.dispatch(
				QuestionsActions.loadQuestionnaire({
					insurerQuestionnaireId: this.insurer.questionnaireId,
					profileQuestionnaireId: this.contract.profile.questionnaireId ?? 0,
					languageCode: this.language.languageCode,
				})
			);

		this.subs.push(
			this.store.pipe(select(getQuestionnaire)).pipe(filterNullOrUndefined()).subscribe((questionnaire) => {
				this.questionnaire = questionnaire;
				this.store.dispatch(ArcHospiActions.questionnaireSelected({ questionnaireId: questionnaire.questionnaireId }));

				//set the first question
				this.store.dispatch(setCurrentQuestion({ data: this.questionnaire.listQuestionAnswers.find((a) => a.hierarchyLevel == 0) }));
			})
		);

		this.subs.push(
			this.store.pipe(select(getCurrentQuestion)).pipe(filterNullOrUndefined()).subscribe((question) => {
				this.currentQuestion = question;

				//set the title with the question
				this.store.dispatch(setTitle({ data: question.text }));

				//set displayed answers
				this.store.dispatch(setCurrentAnswers({ data: question.answers }));
			})
		);

		this.subs.push(
			this.store.pipe(select(getAnswerLightInfo)).pipe(filterNullOrUndefined()).subscribe((lightInfo) => {
				if (lightInfo.continuePlay) {
					//Get next question
					const nextQuestion = this.questionnaire.listQuestionAnswers.find((a) => a.stringLvl == lightInfo.stringLvl && a.type == QuestionType.Question);
					if (nextQuestion == undefined)
						//no next question
						return;
					this.store.dispatch(setCurrentQuestion({ data: nextQuestion }));
				} else {
					this.store.dispatch(setQuestionCompleted({ data: true }));
				}
			})
		);
	}

	ngOnDestroy(): void {
		this.subs.forEach((_) => _.unsubscribe());
	}

	onClickRetour() {
		//Remove current answer from summary
		this.store.dispatch(removeAnswersSummary());

		//Get previous answers from current question
		const previousAnswer = this.questionnaire.listQuestionAnswers.find((a) => a.item == this.currentQuestion.aboveItem);

		//no previous answer => go back
		if (previousAnswer == undefined) {
			this.store.dispatch(setOverwritePreHospiConfirmed({ data: false }));
			this.store.dispatch(setConfirmedSite({ data: false }));
			return;
		}

		//Get previous question
		const previousQuestion = this.questionnaire.listQuestionAnswers.find((a) => a.item == previousAnswer.aboveItem);
		this.store.dispatch(setCurrentQuestion({ data: previousQuestion }));
	}

	onClickQuestion(answer: QuestionAnswersDto) {
		this.answer = answer.stringLvl;

		if (this.scrollToTop) {
			this.scrollToTop.nativeElement.scrollTop = 0
		}

		//Add answer in summary
		this.store.dispatch(addAnswersSummary({ data: { question: this.currentQuestion, answer: answer } }));

		//Validate answer
		this.store.dispatch(
			validateAnswer({
				validateAnswer: {
					answerStringLevel: answer.stringLvl,
					contractId: this.contract.contractId,
					hospitalNo: this.site.hospitalNo,
					languageCode: this.language.languageCode,
					profileId: this.contract.profile.profileId,
					questionnaireId: this.questionnaire.questionnaireId,
					admissionPlannedDate: this.admissionDate.toISOString()
				},
			})
		);
	}
}
